/**
 * Les échéances administratives.
 *
 * Le seul endroit du produit qui regarde une date **qui n'est celle d'aucun
 * document**. Une facture a une échéance, un devis une validité, un chantier
 * une fin prévue — toutes portées par la ligne qu'elles concernent. Une
 * décennale qui expire, une TVA à payer le 20, un certificat RGE à renouveler
 * ne se rattachent à rien. C'est exactement pour ça que personne ne les voit
 * venir.
 *
 * Tout ce fichier est pur, donc éprouvable sans base.
 */

/**
 * Cinq natures, et non une liste ouverte.
 *
 * Elles décident du classement et de ce que le panneau dit. `other` existe pour
 * que rien ne soit refusé — un contrôle Qualiopi, une visite périodique de
 * nacelle — sans prétendre connaître d'avance toutes les obligations d'un
 * artisan.
 */
export type DeadlineKind =
  | "insurance"
  | "tax"
  | "certification"
  | "retention"
  | "other";

/**
 * Le rythme d'une obligation.
 *
 * Purement informatif : rien ne l'exécute. Il sert à proposer la date suivante
 * quand le patron déclare l'échéance honorée. Engendrer des occurrences
 * demanderait un balayage nocturne, donc un mécanisme de plus pour une
 * information que la date porte déjà.
 */
export type DeadlineRecurrence = "none" | "monthly" | "quarterly" | "yearly";

export interface AdminDeadline {
  id: string;
  kind: DeadlineKind;
  label: string;
  /** AAAA-MM-JJ. */
  dueOn: string;
  /** La TVA à payer, la retenue à récupérer. `null` pour une date sans somme. */
  amount: number | null;
  /** Numéro de police, de certificat, de dossier. */
  reference: string | null;
  notes: string | null;
  recurrence: DeadlineRecurrence;
  /** Une retenue de garantie retient un chantier. Les autres natures, non. */
  projectId: string | null;
  projectName: string | null;
}

export const DEADLINE_KIND_LABELS: Record<DeadlineKind, string> = {
  insurance: "Assurance",
  tax: "Fiscal",
  certification: "Certification",
  retention: "Retenue de garantie",
  other: "Autre",
};

export const DEADLINE_RECURRENCE_LABELS: Record<DeadlineRecurrence, string> = {
  none: "Ponctuelle",
  monthly: "Mensuelle",
  quarterly: "Trimestrielle",
  yearly: "Annuelle",
};

/**
 * L'urgence d'une échéance, telle que le panneau la peint.
 *
 * `passee` d'abord, et l'ordre compte : une décennale expirée depuis un mois
 * n'est pas « urgente », elle est un découvert d'assurance — et un chantier
 * commencé sans décennale valide engage la responsabilité personnelle du
 * dirigeant. C'est le seul état de ce produit qui mérite d'être rouge sans
 * discussion.
 */
export type DeadlineUrgency = "passee" | "urgente" | "proche" | "lointaine";

/** En deçà de combien de jours une échéance devient urgente. */
const URGENT_DAYS = 15;
/** Au-delà de combien de jours elle cesse d'occuper le tableau de bord. */
const PROCHE_DAYS = 60;

const DAY_MS = 86_400_000;

/** Jours pleins entre deux dates nues, en UTC. */
export function daysUntil(dueOn: string, today: string): number {
  const a = Date.parse(`${dueOn.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${today.slice(0, 10)}T00:00:00Z`);
  return Math.round((a - b) / DAY_MS);
}

export function deadlineUrgency(dueOn: string, today: string): DeadlineUrgency {
  const jours = daysUntil(dueOn, today);
  if (jours < 0) return "passee";
  if (jours <= URGENT_DAYS) return "urgente";
  return jours <= PROCHE_DAYS ? "proche" : "lointaine";
}

/**
 * Ce que le tableau de bord montre : ce qui arrive, et rien d'autre.
 *
 * Une échéance à huit mois est vraie mais n'appelle aucun geste ; la laisser
 * occuper le panneau ferait qu'on cesse de le lire — même raison que le
 * compteur de points d'attention, qui refuse d'agréger tout ce qui cloche.
 * Le dépassé passe devant, puis le plus proche.
 */
export function upcomingDeadlines(
  deadlines: readonly AdminDeadline[],
  today: string,
  horizonDays = PROCHE_DAYS,
): AdminDeadline[] {
  return deadlines
    .filter((d) => daysUntil(d.dueOn, today) <= horizonDays)
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
}

/** Combien d'échéances sont dépassées ou à moins de quinze jours. */
export function pressingCount(
  deadlines: readonly AdminDeadline[],
  today: string,
): number {
  return deadlines.filter((d) => {
    const u = deadlineUrgency(d.dueOn, today);
    return u === "passee" || u === "urgente";
  }).length;
}

const MOIS_PAR_RECURRENCE: Record<DeadlineRecurrence, number> = {
  none: 0,
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/**
 * La date suivante d'une échéance récurrente, proposée quand elle est honorée.
 *
 * `null` pour une échéance ponctuelle : il n'y a pas de suivante, et en
 * inventer une remettrait sans cesse au tableau de bord une obligation qui
 * n'existe plus.
 *
 * Le jour du mois est **conservé quand il existe**, et ramené au dernier jour
 * du mois sinon. C'est le piège classique : une TVA au 31 janvier reportée d'un
 * mois donne le 28 février, pas le 3 mars. Un `Date` qui déborde reporte tout
 * seul sur le mois suivant, ce qui décalerait toute la série d'une échéance à
 * l'autre.
 */
export function nextOccurrence(
  dueOn: string,
  recurrence: DeadlineRecurrence,
): string | null {
  const mois = MOIS_PAR_RECURRENCE[recurrence];
  if (mois === 0) return null;

  const [y, m, d] = dueOn.slice(0, 10).split("-").map(Number) as [number, number, number];
  const total = (m - 1) + mois;
  const annee = y + Math.floor(total / 12);
  const moisCible = (total % 12) + 1;

  // Jour 0 du mois suivant = dernier jour du mois cible.
  const dernierJour = new Date(Date.UTC(annee, moisCible, 0)).getUTCDate();
  const jour = Math.min(d, dernierJour);

  return `${annee}-${String(moisCible).padStart(2, "0")}-${String(jour).padStart(2, "0")}`;
}
